import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/auth';
import { brandApi } from '../api/client';
import type { Brand } from '@bosamie/shared';

export function Layout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    brandApi.list().then((data) => setBrands(data)).catch(() => {});
  }, [location.pathname]);

  // Determine active nav and breadcrumb from path
  const path = location.pathname;
  const navItems = [
    { label: '数据总览', path: '/dashboard', icon: '📊' },
    { label: '品牌管理', path: '/brands', icon: '👜' },
    { label: '轮播管理', path: '/banners', icon: '🖼️' },
    { label: '信息配置', path: '/config', icon: '⚙️' },
  ];

  const isActive = (itemPath: string) => path === itemPath || path.startsWith(itemPath + '/');

  // Build breadcrumb
  const crumbs: { label: string; to?: string }[] = [{ label: '管理后台', to: '/dashboard' }];
  if (path.startsWith('/brands/')) {
    const parts = path.split('/').filter(Boolean);
    if (parts[1]) {
      const brand = brands.find((b) => b.id === parts[1]);
      if (brand) crumbs.push({ label: brand.name, to: `/brands/${brand.id}` });
    }
  }

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-logo">
          <span className="dot" />
          <span className="sidebar-text">Bosamie Admin</span>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="sidebar-text">{item.label}</span>
            </Link>
          ))}

          {/* Brand tree */}
          <div className="sidebar-tree">
            <div className="tree-label">商品树</div>
            {brands.map((brand) => (
              <div key={brand.id}>
                <Link
                  to={`/brands/${brand.id}`}
                  className={`tree-brand ${path.includes(brand.id) ? 'active' : ''}`}
                >
                  <span>›</span> {brand.name}
                </Link>
              </div>
            ))}
          </div>
        </nav>
      </aside>

      {/* Main */}
      <div className={`main-area ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <header className="topbar">
          <button
            className="btn-icon"
            onClick={() => setCollapsed(!collapsed)}
            title="收起侧边栏"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M6 5L11 10L6 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <nav className="breadcrumb">
            {crumbs.map((c, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span className="cur">{c.label}</span>}
                {i < crumbs.length - 1 && <span>›</span>}
              </span>
            ))}
          </nav>
          <div className="spacer" />
          <button className="btn btn-ghost btn-sm" onClick={logout}>退出登录</button>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
